import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { 
  Shield, Plus, TrendingUp, Building2, Gem, Home as HomeIcon, 
  Lock, ArrowRight, Sparkles, PieChart, BarChart3, Wallet
} from 'lucide-react';

const ZeroStateDashboard = ({ user, setUser, onDataAdded }) => {
  const navigate = useNavigate();
  const [showAssetPicker, setShowAssetPicker] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  
  const isDemoAccount = user?.email === 'demo-free@maadabbu.com' || user?.email === 'demo-premium@maadabbu.com';

  const assetTypes = [
    {
      id: 'shares',
      icon: TrendingUp,
      label: 'Shares / Mutual Funds',
      description: 'Track stocks pledged against loans',
      color: 'emerald',
      action: () => navigate('/transactions')
    },
    {
      id: 'gold',
      icon: Gem,
      label: 'Gold',
      description: 'Track gold loans and pawned jewelry',
      color: 'amber',
      action: () => toast.info('Gold tracking coming soon!')
    },
    {
      id: 'property',
      icon: HomeIcon,
      label: 'Land / Property',
      description: 'Track property mortgages',
      color: 'blue',
      action: () => toast.info('Property tracking coming soon!')
    }
  ];

  const handleAssetSelect = (asset) => {
    setSelectedAsset(asset);
    asset.action();
    setShowAssetPicker(false);
  };

  return (
    <Layout user={user} setUser={setUser}>
      <div className="relative min-h-[80vh] flex flex-col">
        {/* Ghost UI Background - Skeleton of what dashboard will look like */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.03]">
          {/* Fake cards skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-gray-900 rounded-xl" />
            ))}
          </div>
          {/* Fake chart skeleton */}
          <div className="h-64 bg-gray-900 rounded-xl mb-8" />
          {/* Fake table skeleton */}
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-12 bg-gray-900 rounded-lg" />
            ))}
          </div>
        </div>

        {/* Main Content - Centered */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-4 py-12">
          {/* Demo Badge */}
          {isDemoAccount && (
            <div className="absolute top-4 right-4">
              <span className="px-4 py-2 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-sm font-bold rounded-full uppercase tracking-wider shadow-lg animate-pulse">
                Demo Mode - Zero State
              </span>
            </div>
          )}
          
          {/* Vault Illustration */}
          <div className="mb-8 relative">
            <div className="w-32 h-32 md:w-40 md:h-40 rounded-full bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center shadow-lg">
              <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-inner">
                <Shield className="w-12 h-12 md:w-16 md:h-16 text-white" strokeWidth={1.5} />
              </div>
            </div>
            {/* Decorative elements */}
            <div className="absolute -top-2 -right-2 w-8 h-8 bg-amber-400 rounded-full flex items-center justify-center shadow-md">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
          </div>

          {/* Headline */}
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4 max-w-2xl">
            Let's bring your family's wealth under one roof.
          </h1>

          {/* Body Text */}
          <p className="text-gray-600 text-base md:text-lg max-w-xl mb-8 leading-relaxed">
            Track your Gold, Land, and Shares obligations in one place. 
            Stop relying on memory. Whether it's a bank receipt or a broker's slip, 
            add your first loan to start tracking.
          </p>

          {/* Primary CTA */}
          <button
            onClick={() => setShowAssetPicker(true)}
            className="btn-primary text-lg px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105 flex items-center gap-3"
          >
            <Plus className="w-6 h-6" />
            Add My First Loan
          </button>

          {/* Secondary CTA - Disabled for MVP */}
          {/* 
          <button className="mt-4 text-gray-500 hover:text-gray-700 flex items-center gap-2">
            <Camera className="w-5 h-5" />
            Scan a Receipt
          </button>
          */}

          {/* Quick Stats Preview */}
          <div className="mt-12 grid grid-cols-3 gap-4 md:gap-8 max-w-lg">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-emerald-100 flex items-center justify-center">
                <PieChart className="w-6 h-6 text-emerald-600" />
              </div>
              <p className="text-xs text-gray-500">Portfolio Tracking</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-blue-100 flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-blue-600" />
              </div>
              <p className="text-xs text-gray-500">Performance Analytics</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-purple-100 flex items-center justify-center">
                <Wallet className="w-6 h-6 text-purple-600" />
              </div>
              <p className="text-xs text-gray-500">Debt Management</p>
            </div>
          </div>
        </div>

        {/* Trust Signal Footer */}
        <div className="relative z-10 text-center py-6 border-t border-gray-100">
          <p className="text-sm text-gray-400 flex items-center justify-center gap-2">
            <Lock className="w-4 h-4" />
            Your data is encrypted and never shared. We track, we don't transact.
          </p>
        </div>

        {/* Asset Type Picker Modal */}
        {showAssetPicker && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setShowAssetPicker(false)}
            />
            
            {/* Bottom Sheet */}
            <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 p-6 pb-8 shadow-2xl animate-slide-up">
              <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mb-6" />
              
              <h2 className="text-xl font-bold text-gray-800 mb-2 text-center">
                What type of asset is pledged?
              </h2>
              <p className="text-gray-500 text-sm mb-6 text-center">
                Select the type of collateral for your loan
              </p>

              <div className="space-y-3">
                {assetTypes.map((asset) => {
                  const Icon = asset.icon;
                  return (
                    <button
                      key={asset.id}
                      onClick={() => handleAssetSelect(asset)}
                      className={`w-full p-4 rounded-xl border-2 border-gray-100 hover:border-${asset.color}-300 hover:bg-${asset.color}-50 transition-all flex items-center gap-4 text-left group`}
                    >
                      <div className={`w-14 h-14 rounded-xl bg-${asset.color}-100 flex items-center justify-center group-hover:bg-${asset.color}-200 transition-colors`}>
                        <Icon className={`w-7 h-7 text-${asset.color}-600`} />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-800">{asset.label}</p>
                        <p className="text-sm text-gray-500">{asset.description}</p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setShowAssetPicker(false)}
                className="w-full mt-4 py-3 text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>

      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </Layout>
  );
};

export default ZeroStateDashboard;
